<template>
  <div class="w-full select-none relative" ref="containerRef">
    <!-- Base track line (glassified) -->
    <div class="absolute rounded journey-track" :style="trackStyle"></div>
    <!-- Animated progress line (spring) -->
    <div
      class="absolute rounded journey-progress"
      ref="progressRef"
      :style="progressBaseStyle"
    ></div>
    <div class="flex items-start justify-between gap-6 md:gap-10">
      <div v-for="(s, i) in steps" :key="s.key" class="flex-1 flex flex-col items-center">
        <div class="w-full flex items-center justify-center">
          <!-- Icon image (white assets preferred) -->
          <div
            class="shrink-0 flex items-center justify-center"
            :title="s.label"
            :ref="(el) => setIconRef(el, i)"
          >
            <img
              :src="s.iconSrc"
              alt=""
              class="h-12"
              :class="iconFilter(i)"
              :style="iconStyle(steps[i].key)"
            />
          </div>
        </div>
        <!-- Node circle row aligned with icons (fills as progress passes) -->
        <div class="mt-1 mb-1 h-5 flex items-center justify-center relative z-20">
          <div
            :class="['rounded-full', nodeClass(i)]"
            :style="nodeStyle(i)"
            :ref="(el) => setNodeRef(el, i)"
          ></div>
        </div>
        <!-- Label: consistent height, tighter leading -->
        <div class="mt-2 text-center min-h-[2.25rem] flex items-start justify-center">
          <span
            :class="[
              'max-w-28 md:max-w-40 leading-tight text-[10px] md:text-xs',
              isActive(i) ? 'text-white' : 'text-slate-400',
            ]"
            >{{ s.label }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { animate, spring } from 'motion'

const props = defineProps<{
  status: string
  /** When true, light up return_label_created even if status hasn't reached it */
  hasReturnLabel?: boolean
}>()

const steps = [
  {
    key: 'reserved',
    label: 'Reserved',
    short: 'Reserve',
    iconSrc: '/images/reserve-white.png',
    major: true,
  },
  {
    key: 'checked_out',
    label: 'Checked Out',
    short: 'Checked Out',
    iconSrc: '/images/checkin-white.svg',
    major: true,
  },
  {
    key: 'outbound_label',
    label: 'Outbound Label',
    short: 'Label',
    iconSrc: '/images/outbound-label.svg',
    major: true,
  },
  {
    key: 'carrier_accepted',
    label: 'Carrier Accepted',
    short: 'Pickup',
    iconSrc: '/images/outbound-in-transit.svg',
    major: true,
  },
  {
    key: 'outbound_in_transit',
    label: 'In Transit',
    short: 'Ship→',
    iconSrc: '/images/outbound-in-transit.svg',
    major: true,
  },
  {
    key: 'out_for_delivery',
    label: 'Out for Delivery',
    short: 'OFD',
    iconSrc: '/images/delivered.svg',
    major: true,
  },
  {
    key: 'with_borrower',
    label: 'With Borrower',
    short: 'Delivered',
    iconSrc: '/images/delivered.svg',
    major: true,
  },
  {
    key: 'return_initiated',
    label: 'Return Label Created',
    short: 'Return Label',
    iconSrc: '/images/return-label-printed.svg',
  },
  {
    key: 'return_carrier_scan',
    label: 'Return Carrier Scan',
    short: 'Pickup',
    iconSrc: '/images/Return-in-transit.svg',
  },
  {
    key: 'return_in_transit',
    label: 'Return In Transit',
    short: '←Ship',
    iconSrc: '/images/Return-in-transit.svg',
  },
  {
    key: 'received',
    label: 'Received at Warehouse',
    short: 'Recv',
    iconSrc: '/images/received-at-warehouse.svg',
  },
  {
    key: 'checked_in',
    label: 'Checked In',
    short: 'Check-in',
    iconSrc: '/images/checkin-white.svg',
  },
]

/**
 * The "return_initiated" step index — used to light up the node when
 * a return label was created alongside the outbound label (scan-based).
 */
const returnInitiatedIdx = steps.findIndex((s) => s.key === 'return_initiated')

const progressIndex = computed(() => {
  const idx = steps.findIndex((s) => s.key === (props.status || '').toLowerCase())
  return idx >= 0 ? idx : 0
})

function isDone(i: number) {
  return i < progressIndex.value
}
function isActive(i: number) {
  if (i === progressIndex.value) return true
  // Also mark Return Label Created as active-styled when hasReturnLabel is true
  if (i === returnInitiatedIdx && props.hasReturnLabel && progressIndex.value < returnInitiatedIdx) return true
  return false
}
function iconFilter(i: number) {
  const k = steps[i].key
  return k === 'reserved' || k === 'checked_in' ? '' : 'icon-white'
}
function iconStyle(key: string) {
  // Normalize width: compress certain wide SVGs slightly; make reserve a touch smaller
  if (key === 'reserved') return { transform: 'scale(0.9)' }
  const compress = new Set([
    'outbound_label',
    'with_borrower',
    'return_initiated',
    'received',
    'checked_in',
  ])
  if (compress.has(key)) return { transform: 'scaleX(0.92)' }
  return {}
}

function nodeClass(i: number) {
  // Fill nodes up to and including the active step
  if (i <= progressIndex.value) return 'bg-primary-500 shadow-[0_0_6px_rgba(70,177,225,0.5)]'
  // Light up "Return Label Created" when a return label exists (scan-based, created with outbound)
  if (i === returnInitiatedIdx && props.hasReturnLabel && progressIndex.value < returnInitiatedIdx) {
    return 'bg-primary-500 shadow-[0_0_6px_rgba(70,177,225,0.5)]'
  }
  return 'bg-slate-700'
}
function nodeStyle(i: number) {
  // Show nodes at all steps; slightly larger for primary milestones
  const majorKeys = new Set(['reserved', 'checked_out', 'outbound_label', 'carrier_accepted', 'outbound_in_transit', 'out_for_delivery', 'with_borrower'])
  const size = majorKeys.has(steps[i].key) ? 18 : 14
  return { width: size + 'px', height: size + 'px' }
}

// Track and animated progress bar styles
const containerRef = ref<HTMLElement | null>(null)
const progressRef = ref<HTMLElement | null>(null)
const iconEls: (HTMLElement | null)[] = []
const nodeEls: (HTMLElement | null)[] = []
// Vue passes Element | ComponentPublicInstance | null; store HTMLElement if present
const setIconRef = (el: Element | any | null, i: number) => {
  iconEls[i] = (el as unknown as HTMLElement) || null
}
const setNodeRef = (el: Element | any | null, i: number) => {
  nodeEls[i] = (el as unknown as HTMLElement) || null
}

const trackTop = 62 // vertical position of track (below icons, centered on node)
const trackLeft = ref(0)
const trackWidth = ref(0)
const trackStyle = computed(() => ({
  left: trackLeft.value + 'px',
  width: trackWidth.value + 'px',
  top: trackTop + 'px',
  height: '4px',
}))
const progressBaseStyle = computed(() => ({
  left: trackLeft.value + 'px',
  top: trackTop + 'px',
  height: '4px',
  width: '0px',
}))

function animateProgress() {
  const el = progressRef.value
  const root = containerRef.value
  if (!el || !root) return
  // compute width from first node center to current node center
  const first = nodeEls[0]
  const current = nodeEls[progressIndex.value]
  if (!first || !current) return
  const leftRoot = root.getBoundingClientRect().left
  const firstCenter = first.getBoundingClientRect().left + first.offsetWidth / 2 - leftRoot
  const currentCenter = current.getBoundingClientRect().left + current.offsetWidth / 2 - leftRoot
  const target = Math.max(0, currentCenter - firstCenter)
  // Slower, ease-out finish
  animate(
    el as HTMLElement,
    { width: `${target}px` } as any,
    { duration: 1.6, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' } as any,
  )

  // icon pop (gentler)
  iconEls.forEach((n, idx) => {
    if (n)
      animate(
        n as HTMLElement,
        { transform: `scale(${idx === progressIndex.value ? 1.08 : 1.0})` } as any,
        { duration: 0.5, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' } as any,
      )
  })
}

onMounted(() => {
  const computeTrack = () => {
    const root = containerRef.value
    const first = nodeEls[0]
    const last = nodeEls[nodeEls.length - 1]
    if (!root || !first || !last) return
    const leftRoot = root.getBoundingClientRect().left
    const l = first.getBoundingClientRect().left + first.offsetWidth / 2 - leftRoot
    const r = last.getBoundingClientRect().left + last.offsetWidth / 2 - leftRoot
    trackLeft.value = l
    trackWidth.value = Math.max(0, r - l)
  }
  computeTrack()
  animateProgress()
  const onResize = () => {
    computeTrack()
    animateProgress()
  }
  window.addEventListener('resize', onResize)
  ;(onBeforeUnmount as any)(() => window.removeEventListener('resize', onResize))
})

watch(progressIndex, () => animateProgress())
</script>

<style scoped>
.hover\:scale-105:hover {
  transform: scale(1.05);
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.icon-white {
  filter: invert(1) brightness(1.2) contrast(1.1);
}
.journey-track {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0.08));
  backdrop-filter: blur(2px);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}
.journey-progress {
  background: #46b1e1;
  box-shadow: 0 0 16px rgba(70, 177, 225, 0.45);
}
</style>
